import LogoSlack16 from '@carbon/icons-react/es/logo--slack/16';
import Money16 from '@carbon/icons-react/es/money/16';
import TaskView16 from '@carbon/icons-react/es/task--view/16';
import UserMultiple16 from '@carbon/icons-react/es/user--multiple/16';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Redirect, Switch, useParams } from 'react-router-dom';
import { SlackConfig } from 'api/integrations/slack/slack.types';
import { ProjectId } from 'api/models';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { TaskSettings16 } from 'components/atoms/icons/task-settings';
import { PageLoading } from 'components/atoms/loading';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { SwitchContentBasedOnAccess } from 'components/organisms/permission-controller';
import {
  GeneralInformation,
  ProjectSettingsPrompt,
  ProjectVisibility,
  SlackNotifications,
  TeamMembers,
  BudgetAndRates,
} from 'components/organisms/projects/project-settings';
import { Krypton } from 'components/templates/krypton';
import { PermissionRuleName } from 'constant';
import { CurrentProjectContext } from 'contexts';
import { ProjectFinancialsSettingsContext } from 'contexts/project-financials-settings';
import { useProjectDetail } from 'data/projects';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useProjectFinancialsSettings } from 'hooks/use-project-financials-settings';
import { useTranslation } from 'hooks/use-translation';
import { useProjectWebsocket } from 'hooks/websockets';
import { useFinancialFieldsQuery } from 'queries';
import {
  useQuerySlackAuthMeta,
  useQuerySlackConfig,
} from 'queries/slack-integration';
import { SecureRoute } from 'routes/route-with-permission-check';
import { getProjectDetailUrl } from 'utils/urls';
import { ProjectDetailError } from '../error';
import { StyledBreadcrumb } from './styled';

const getConfig = (data?: SlackConfig[]) => data?.[0];

export const ProjectSettings: FC = () => {
  const title = useTranslation('projects.settings');
  const { projectId } = useParams<{ projectId: string }>();
  const currentProjectId = ProjectId(Number(projectId));
  const { status: metaStatus } = useQuerySlackAuthMeta();
  const { data, isLoading: isSlackLoading } = useQuerySlackConfig({
    enabled: metaStatus === 'success',
  });
  const { data: financialFieldsLookupByName } = useFinancialFieldsQuery();

  const slackConfig = getConfig(data);

  useProjectWebsocket(currentProjectId);
  const {
    data: project,
    isLoading,
    error,
  } = useProjectDetail(currentProjectId);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);

  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
  const [accountability] = useAccountFeature(AccountFeature.Accountability);

  const financialsContext = useProjectFinancialsSettings({
    projectId: parseInt(projectId, 10),
    financialFieldsLookupByName,
  });

  const { permissionsMap } = financialsContext;
  const {
    projectBudget: { canRead: canReadBudget },
    projectHours: { canRead: canReadHours },
    projectRateCardUpdate: { canRead: canReadRateCard },
    projectCustomBillRate: { canRead: canReadCustomBillRates },
  } = permissionsMap;

  const canViewRateCardSettings = useMemo(
    () =>
      accountability &&
      isFinancialsEnabled &&
      canReadBudget &&
      canReadHours &&
      canReadRateCard &&
      canReadCustomBillRates,
    [
      accountability,
      canReadBudget,
      canReadCustomBillRates,
      canReadHours,
      canReadRateCard,
      isFinancialsEnabled,
    ],
  );

  const path = `/projects/${projectId}/settings`;
  const { isAllowedForCurrentUser } = usePermissionForRule();

  const navLinkObj = [
    {
      value: [
        {
          menuName: (
            <FormattedMessage id="project.settings.general_information" />
          ),
          menuIcon: <TaskSettings16 />,
          route: `${path}/details`,
          ruleName: PermissionRuleName.ProjectSettingDetailPage,
        },
        {
          menuName: (
            <span data-cy="project_settings.project_visiblity">
              <FormattedMessage id="project.settings.project_visibility" />
            </span>
          ),
          menuIcon: <TaskView16 />,
          route: `${path}/visibility`,
          ruleName: PermissionRuleName.ProjectSettingVisibilityPage,
        },
        ...(canViewRateCardSettings
          ? [
              {
                menuName: 'Budget and rates',
                menuIcon: <Money16 />,
                route: `${path}/financials`,
              },
            ]
          : []),
        {
          menuName: <FormattedMessage id="team_members" />,
          menuIcon: <UserMultiple16 />,
          route: `${path}/members`,
        },

        ...(slackConfig
          ? [
              {
                menuName: (
                  <FormattedMessage id="project.settings.slack.notification" />
                ),
                menuIcon: <LogoSlack16 />,
                route: `${path}/slack-notifications`,
              },
            ]
          : []),
      ].filter(
        ({ ruleName }) => ruleName == null || isAllowedForCurrentUser(ruleName),
      ),
    },
  ];

  return isLoading || isSlackLoading || financialFieldsLookupByName == null ? (
    <PageLoading />
  ) : project ? (
    <CurrentProjectContext.Provider value={project}>
      <Krypton
        documentTitle={title}
        title={title}
        leftMenuItems={navLinkObj}
        header={
          <>
            <HideForCustomer>
              <StyledBreadcrumb>
                <Breadcrumb.Item>
                  <Link to={getProjectDetailUrl(currentProjectId)}>
                    {project.projectName}
                  </Link>
                </Breadcrumb.Item>
              </StyledBreadcrumb>
            </HideForCustomer>
          </>
        }
      >
        <ProjectSettingsPrompt
          isDirty={isDirty}
          onReset={() => setIsDirty(false)}
        />
        <Switch>
          <SecureRoute
            path={`${path}/details`}
            render={() => <GeneralInformation setDirty={setIsDirty} />}
            ruleName={PermissionRuleName.ProjectSettingDetailPage}
          />
          <SecureRoute
            path={`${path}/visibility`}
            render={() => <ProjectVisibility setDirty={setIsDirty} />}
            ruleName={PermissionRuleName.ProjectSettingVisibilityPage}
          />
          <SecureRoute
            path={`${path}/members`}
            render={() => <TeamMembers project={project} />}
          />
          {canViewRateCardSettings && (
            <SecureRoute
              path={`${path}/financials`}
              render={() => (
                <ProjectFinancialsSettingsContext.Provider
                  value={financialsContext}
                >
                  <BudgetAndRates project={project} />
                </ProjectFinancialsSettingsContext.Provider>
              )}
            />
          )}

          {slackConfig ? (
            <SecureRoute
              path={`${path}/slack-notifications`}
              render={() => (
                <SlackNotifications
                  projectId={Number(projectId)}
                  config={slackConfig}
                />
              )}
            />
          ) : null}

          <SecureRoute path="*" exact>
            <SwitchContentBasedOnAccess
              ruleName={PermissionRuleName.ProjectSettingDetailPage}
              hasAccess={<Redirect to={`${path}/details`} />}
              doesNotHaveAccess={<Redirect to={`${path}/members`} />}
            />
          </SecureRoute>
        </Switch>
      </Krypton>
    </CurrentProjectContext.Provider>
  ) : (
    error && <ProjectDetailError error={error} />
  );
};
